var FadeSupport       = require("enyo-ui/FadeSupport"),
    ScrollButton      = require("enyo-ui/ScrollButton"),
    ScrollMap         = require("enyo-ui/ScrollMap"),
    ScrollThumb       = require("enyo-ui/ScrollThumb"),
    ScrollerDecorator = require("enyo-ui/ScrollerDecorator"),
    ScrollerDecoratorCarousel,
    kind              = require("enyo/kind"),
    logger            = require("enyo/logger"),
    utils             = require("enyo/utils");

require("enyo-ui");

/**
 * [ScrollerDecoratorCarousel]{@link enyo-ui.ScrollerDecoratorCarousel} is
 * scroller decorator that displays [ScrollButtons]{@link enyo-ui.ScrollButton}
 * for one axis, which scroll the [Scroller]{@link enyo-ui.Scroller} when you
 * press them.
 *
 * This control can also optionally display a scroll thumb as indicator of the
 * current scroll position.
 *
 * @ui
 * @class   enyo-ui.ScrollerDecoratorCarousel
 * @extends enyo-ui.ScrollerDecorator
 * @public
 */
ScrollerDecoratorCarousel = module.exports = kind(/** @lends  enyo-ui.ScrollerDecoratorCarousel.prototype */ {

    /**
     * @private
     */
    name: "enyo-ui.ScrollerDecoratorCarousel",

    /**
     * @private
     */
    kind: ScrollerDecorator,

    /**
     * @private
     */
    classes: "enyo-ui-scroller-decorator-carousel",

    /**
     * When to show the scrollThumb:
     *  - "always"   Always shows the thumb
     *  - "onscroll" Only shows the thumb when scroller is scrolling
     *  - "never"    Never shows the thumb
     *
     * @type    {string}
     * @default "onscroll"
     * @public
     */
    showScrollThumb: "onscroll",

    /**
     * The kind to use for scroll buttons.
     *
     * @type    {enyo.Control}
     * @default enyo-ui.ScrollButton
     * @public
     */
    scrollButtonKind: ScrollButton,

    /**
     * Options for the scroll button used to scroll backward.
     *
     * @type {?object}
     * @default null
     * @public
     */
    scrollButtonBackwardOptions: null,

    /**
     * Options for the scroll button used to scroll forward.
     *
     * @type {?object}
     * @default null
     * @public
     */
    scrollButtonForwardOptions: null,

    /**
     * The kind to use as scroll track.
     *
     * @type    {enyo.Control}
     * @default enyo-ui.ScrollMap
     * @public
     */
    scrollTrackKind: ScrollMap,

    /**
     * Options for the scroll track.
     *
     * @type {?object}
     * @public
     */
    scrollTrackOptions: {
        shouldScrollOnTap: false
    },

    /**
     * The kind to use as scroll thumb.
     *
     * @type    {enyo.Control}
     * @default enyo-ui.ScrollThumb
     * @public
     */
    scrollThumbKind: ScrollThumb,

    /**
     * Options for the scroll thumb.
     *
     * @type {?object}
     * @public
     */
    scrollThumbOptions: null,

    /**
     * Flag if the decorator should trigger a resize if it applied changes in
     * the chrome configuration.
     *
     * @type {boolean}
     * @public
     */
    resizeOnChromeConfigurationChange: false,

    /**
     * @private
     */
    bindings: [{
        from: ".scroller",
        to:   ".$.scrollButtonBackward.scroller"
    }, {
        from: ".scroller",
        to:   ".$.scrollButtonForward.scroller"
    }, {
        from: ".scroller",
        to:   ".$.scrollTrack.scroller"
    }, {
        from: ".scroller",
        to:   ".$.scrollThumb.scroller"
    }, {
        from: ".$.scrollTrack",
        to:   ".$.scrollThumb.track"
    }],

    /**
     * @private
     */
    observers: {
        configureChrome:     ["axis", "showScrollThumb"],
        toggleScrollButtons: ["scrolling", "scrollPosition", "scrollLimits"]
    },

    /**
     * Builds the scrollbar track and scroll buttons.
     *
     * @private
     */
    createDecoration: function() {
        this.createScrollButtons();
        this.createScrollTrack();
    },

    /**
     * Builds the vertical scrollbar.
     *
     * @private
     */
    createScrollButtons: function() {
        var scrollButtonBackwardOptions = this.scrollButtonBackwardOptions || {},
            scrollButtonForwardOptions  = this.scrollButtonForwardOptions || {};

        this.controlParent = null;

        // Scroll button backward
        this.createComponent({
            name:     "scrollButtonBackward",
            kind:     this.scrollButtonKind,
            mixins:   [FadeSupport],
            classes:  "enyo-ui-scroller-decorator-carousel__scroll-button-backward " + (scrollButtonBackwardOptions.classes || ""),
            isChrome: true
        }, scrollButtonBackwardOptions);
        this.$.scrollButtonBackward.hide(true, true);

        // Scroll button forward
        this.createComponent({
            name:     "scrollButtonForward",
            kind:     this.scrollButtonKind,
            mixins:   [FadeSupport],
            classes:  "enyo-ui-scroller-decorator-carousel__scroll-button-forward " + (scrollButtonForwardOptions.classes || ""),
            isChrome: true
        }, scrollButtonForwardOptions);
        this.$.scrollButtonForward.hide(true, true);

    },

    /**
     * Builds the scroll track.
     *
     * @private
     */
    createScrollTrack: function() {
        var scrollTrack,
            scrollTrackOptions = this.scrollTrackOptions || {},
            scrollThumbOptions = this.scrollThumbOptions || {};

        // Scroll bar
        this.controlParent = null;
        scrollTrack        = this.createComponent({
            name:     "scrollTrack",
            kind:     this.scrollTrackKind,
            mixins:   [FadeSupport],
            classes:  "enyo-ui-scroller-decorator-carousel__scroll-track " + (scrollTrackOptions.classes || ""),
            isChrome: true
        }, scrollTrackOptions);

        // Scroll thumb
        this.controlParent = scrollTrack;
        this.createComponent({
            name:     "scrollThumb",
            kind:     this.scrollThumbKind,
            classes:  "enyo-ui-scroller-decorator-carousel__scroll-thumb " + (scrollThumbOptions.classes || ""),
            isChrome: true
        }, scrollThumbOptions);

        // Track visibility
        switch (this.showScrollThumb) {
            case "always":
                scrollTrack.show(true, true);
                break;
            case "never":
            case "onscroll":
                scrollTrack.hide(true, true);
                break;
            default:
                // No-op, feel free to extend with more options.
                break;
        }
    },

    /**
     * @method
     * @private
     */
    connectScroller: kind.inherit(function(sup) {
        return function(scroller) {
            sup.apply(this, arguments);
            scroller.observe("horizontal", this.resolveAxis, this);
            scroller.observe("vertical", this.resolveAxis, this);
            this.resolveAxis();
        };
    }),

    /**
     * @method
     * @private
     */
    disconnectScroller: kind.inherit(function(sup) {
        return function() {
            var scroller = this.getScroller();

            if (scroller) {
                scroller.unobserve("horizontal", this.resolveAxis);
                scroller.unobserve("vertical", this.resolveAxis);
            }
            sup.apply(this, arguments);
        };
    }),

    /**
     * Resolves the scroll direction.
     *
     * @private
     */
    resolveAxis: function() {
        var scroller = this.getScroller(),
            vertical,
            horizontal;

        if (!scroller) {
            this.set("axis", null);

            return;
        }

        vertical   = scroller.get("vertical");
        horizontal = scroller.get("horizontal");

        // Configure chrome for horizontal scroll
        if (!vertical && horizontal) {
            this.set("axis", "horizontal");
        }

        // Configure chrome for vertical scroll
        else if (vertical && !horizontal) {
            this.set("axis", "vertical");
        }

        // Both directions is not supported
        else if (vertical && horizontal) {
            this.set("axis", null);
            logger.error("ScrollerDecoratorCarousel doesn't support scrolling on both axis");
        }
        else {
            this.set("axis", null);
        }
    },

    // ...........................
    // Chrome Configuration & Synchronization

    /**
     * Calculates the direction of the scroll buttons, the axis of the scroll
     * thumb and the visibility of the scroll track.
     *
     * @return {Object}  The chrome configuration.
     * @private
     */
    calcChromeConfiguration: function() {
        var scroller      = this.getScroller(),
            axis          = this.get("axis"),
            configuration = {
                showScrollTrack:               this.showScrollThumb,
                scrollButtonBackwardDirection: null,
                scrollButtonForwardDirection:  null,
                scrollTrackAxis:               null
            };

        // No scroller or no scrolling, no chrome
        if (!scroller || !axis) {
            return configuration;
        }

        // Configure chrome for current axis
        switch (axis) {
            case "horizontal":
                configuration.scrollButtonBackwardDirection = "left";
                configuration.scrollButtonForwardDirection  = "right";
                configuration.scrollTrackAxis               = "horizontal";
                break;
            case "vertical":
                configuration.scrollButtonBackwardDirection = "up";
                configuration.scrollButtonForwardDirection  = "down";
                configuration.scrollTrackAxis               = "vertical";
                break;
            default: // Do nothing
        }

        return configuration;
    },

    /**
     * Apply the chrome configuration.
     *
     * @param  {Object}   configuration  The chrome configuration to apply
     * @return {Boolean}  Whether the new chrome configuration requires a
     *                    reflow.
     * @private
     */
    applyChromeConfiguration: function(configuration) {
        var client  = this.$.client,
            changed = false;

        this.$.scrollButtonBackward.set("direction", configuration.scrollButtonBackwardDirection);
        this.$.scrollButtonForward.set("direction", configuration.scrollButtonForwardDirection);
        this.$.scrollTrack.set("axis", configuration.scrollTrackAxis);
        this.$.scrollThumb.set("axis", configuration.scrollTrackAxis);

        if (configuration.showScrollTrack === "always") {
            this.showScrollTrack();
            changed = true;
        }
        else if (configuration.showScrollTrack === "never") {
            this.hideScrollTrack();
            changed = true;
        }

        if (changed) {
            this.resizeOnChromeConfigurationChange && this.resize();

            return client.didResize;
        }
        else {
            return false;
        }
    },

    /**
     * Synchronizes the thumb with scroller.
     *
     * @private
     */
    syncChrome: function() {
        this.$.scrollThumb.sync();
    },

    /**
     * Toggles scrollThumb visibility if set to "onscroll".
     *
     * @param {boolean}  wasScrolling  The old value
     * @private
     */
    scrollingChanged: function(wasScrolling) {
        var isScrolling = this.scrolling;

        if (isScrolling !== wasScrolling) {
            if (this.showScrollThumb === "onscroll") {
                this[this.scrolling ? "showScrollTrack" : "hideScrollTrack"]();
            }
        }
    },

    /**
     * Evaluates whether to show buttons when scroller stops scrolling or
     * changes dimensions.
     *
     * @private
     */
    toggleScrollButtons: function() {
        var scroller  = this.getScroller(),
            scrolling = this.scrolling,
            axis      = this.get("axis");

        // Buttons only visible when there is something to scroll to
        if (scroller && !scrolling) {
            if (axis === "horizontal") {
                scroller.isAtLeft() ? this.disableScrollButtonBackward() : this.enableScrollButtonBackward();
                scroller.isAtRight() ? this.disableScrollButtonForward() : this.enableScrollButtonForward();
            }
            else if (axis === "vertical") {
                scroller.isAtTop() ? this.disableScrollButtonBackward() : this.enableScrollButtonBackward();
                scroller.isAtBottom() ? this.disableScrollButtonForward() : this.enableScrollButtonForward();
            }
        }
    },

    // ...........................
    // Toggle Chrome

    /**
     * Shows the scrollTrack control by fading it in. Override if you want
     * another kind of transition.
     *
     * @public
     */
    showScrollTrack: function() {
        this.$.scrollTrack.show(true);
    },

    /**
     * Hides the scrollTrack control by fading it out. Override if you want
     * another kind of transition.
     *
     * @public
     */
    hideScrollTrack: function() {
        this.$.scrollTrack.hide(true);
    },

    /**
     * Enables the scrollButtonForward control and by fades it in. Override if
     * you want another kind of transition.
     *
     * @public
     */
    enableScrollButtonForward: function() {
        var scrollButtonForward = this.$.scrollButtonForward;

        scrollButtonForward.set("disabled", false);
        scrollButtonForward.show(true);
    },

    /**
     * Disables the scrollButtonForward control and fades it out. Override if
     * you want another kind of transition.
     *
     * @public
     */
    disableScrollButtonForward: function() {
        var scrollButtonForward = this.$.scrollButtonForward;

        scrollButtonForward.set("disabled", true);
        scrollButtonForward.hide(true);
    },

    /**
     * Enables the scrollButtonBackward control and by fades it in. Override if
     * you want another kind of transition.
     *
     * @public
     */
    enableScrollButtonBackward: function() {
        var scrollButtonBackward = this.$.scrollButtonBackward;

        scrollButtonBackward.set("disabled", false);
        scrollButtonBackward.show(true);
    },

    /**
     * Disables the scrollButtonBackward control and fades it out. Override if
     * you want another kind of transition.
     *
     * @public
     */
    disableScrollButtonBackward: function() {
        var scrollButtonBackward = this.$.scrollButtonBackward;

        scrollButtonBackward.set("disabled", true);
        scrollButtonBackward.hide(true);
    }

});

/**
 * This method allows enyo.kind to extend the `scrollButtonBackwardOptions`,
 * `scrollButtonForwardOptions`, `scrollTrackOptions` and `scrollThumbOptions`
 * hashes when creating super kinds.
 *
 * @param    {Function}  ctor   The constructor
 * @param    {Object}    props  The properties
 * @memberof Repeater
 * @static
 * @private
 */
ScrollerDecoratorCarousel.concat = function(ctor, props) {
    var proto   = ctor.prototype || ctor,
        options = [
            "scrollButtonBackwardOptions",
            "scrollButtonForwardOptions",
            "scrollTrackOptions",
            "scrollThumbOptions"
        ];

    options.forEach(function(prop) {
        var extension = proto[prop] ? utils.clone(proto[prop]) : {};
        proto[prop]   = utils.mixin(extension, props[prop]);
        delete props[prop];
    });
};
